import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { ExternalLink, Home } from "lucide-react";
import { ReactComponent as Share } from "../../assets/images/share.svg";

function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}

/**
 * PageHeader
 * - breadcrumb: string | ReactNode (ถ้าไม่ส่ง จะ auto จาก path)
 * - icon: React component (lucide) เช่น Package, Home...
 * - title: string
 * - subtitle: string | ReactNode
 *
 * Right actions:
 * - showExport: boolean
 * - exportLabel: string
 * - exportPath: string  (ถ้าเป็น route ในระบบให้ส่งเป็น path; ถ้าเป็นลิงก์ภายนอกก็ได้)
 * - onExport: () => void  (ถ้าส่งมา จะใช้ callback แทนการเปิดลิงก์)
 * - extraActions: ReactNode (ปุ่มอื่นๆด้านขวา)
 */
export default function PageHeader({
  breadcrumb,
  icon: Icon = Home,
  title,
  subtitle,
  showExport = true,
  exportLabel = "Export",
  exportPath = "",
  onExport,
  extraActions = null,
  className = "",
}) {
  const location = useLocation();

  const autoCrumb = useMemo(() => {
    const path = location.pathname;
    // แปลง /dashboard/pricing-queue => หน้าหลัก / Pricing queue (แบบง่าย)
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return "หน้าหลัก";
    const pretty = parts
      .map((p) =>
        p
          .replace(/-/g, " ")
          .replace(/\b\w/g, (m) => m.toUpperCase())
      )
      .join(" / ");
    return `หน้าหลัก / ${pretty}`;
  }, [location.pathname]);

  const crumb = breadcrumb ?? autoCrumb;

  const handleExport = () => {
    if (!showExport) return;
    if (onExport) return onExport();

    if (!exportPath) return;
    // ถ้าเป็นลิงก์ภายนอก
    if (/^https?:\/\//.test(exportPath)) {
      window.open(exportPath, "_blank", "noopener,noreferrer");
      return;
    }
    // ถ้าเป็น path ในระบบก็เปิดแท็บใหม่แบบง่าย (หรือจะใช้ navigate ก็ได้)
    window.open(exportPath, "_blank");
  };

  return (
    <div className={cx("space-y-3", className)}>
      {/* line 1: breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-[#1D1616B2]">
        {/* <Home size={16} className="text-[#1D1616B2]" /> */}
        <Icon size={16} className="text-[#1D1616B2]" />
        <div className="truncate">{crumb}</div>
      </div>

      {/* line 2 */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        {/* left title */}
        <div className="flex items-start gap-3">
          {/* <div className="mt-1 h-9 w-9 rounded-xl bg-slate-900/5 flex items-center justify-center">
            <Icon size={18} className="text-slate-700" />
          </div> */}

          <div className="min-w-0">
            <div className="text-2xl md:text-4xl font-semibold tracking-tight truncate">
              {title}
            </div>
            {subtitle ? (
              <div className="mt-1 flex items-center gap-2 text-[#1D1616B2]">
                {typeof subtitle === "string" ? (
                  <span className="text-sm md:text-base truncate">{subtitle}</span>
                ) : (
                  subtitle
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* right actions */}
        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-3">
          {showExport && (
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-[#1D16161A] bg-white justify-center py-2 px-4 font-semibold active:scale-[0.98] hover:bg-[#F5F5F5] hover:border-[#F5F5F5] hover:text-[#047DED]"
            >
              {exportLabel}
              <Share width={20} height={20}/>
            </button>
          )}

          {extraActions}
        </div>
      </div>

      {/* divider */}
      {/* <div className="h-[1px] w-full bg-slate-200/70" /> */}
    </div>
  );
}
